import { Box, Button, FormControl, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, Typography, useMediaQuery, useTheme } from "@mui/material";
import { AxiosError } from "axios";
import { ChartData, ChartOptions, Point, TimeUnit } from "chart.js";
import { FC, useEffect, useRef, useState } from "react";
import CustomLoader from "../../../../../common/components/CustomLoader";
import CustomSnackbar from "../../../../../common/components/CustomSnackbar";
import { useLoading } from "../../../../../common/hooks/useLoading";
import { IBeehiveStats } from "../../../../../common/interfaces/beehiveStats";
import { ICustomOption } from "../../../../../common/interfaces/form";
import beehiveStatsService from "../../../../../common/services/beehivesStatsService";


import { Block, Error } from "@mui/icons-material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';
import { endOfDay, endOfMonth, format, startOfDay, startOfMonth, subMonths } from "date-fns";
import { enGB } from 'date-fns/locale/en-GB';
import { it } from 'date-fns/locale/it';
import { sl } from 'date-fns/locale/sl';
import I18n from 'i18next';
import { Line } from "react-chartjs-2";
import { ChartJSOrUndefined } from "react-chartjs-2/dist/types";
import { useTranslation, withTranslation } from "react-i18next";
import { IBeehive } from "../../../../../common/interfaces/beehive";

Chart.register(...registerables, zoomPlugin);

interface IChartsTabProps {
    beehiveId: string | undefined;
    parentDialogOpen: boolean;
    beehive: IBeehive | undefined;
}

const ChartsTab: FC<IChartsTabProps> = (props) => {
    const { beehiveId, parentDialogOpen, beehive } = props;

    // global
    const { loading } = useLoading();
    const { t } = useTranslation();
    const locale = I18n.language;
    const [language, setLanguage] = useState(locale);
    const theme = useTheme();
    const largeMediumScreen = useMediaQuery(theme.breakpoints.up("md"));

    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [showDatePickers, setShowDatePickers] = useState<boolean>(false);
    const [currentBeehiveStats, setCurrentBeehiveStats] = useState<IBeehiveStats[]>([]);
    const [temperatureData, setTemperatureData] = useState<ChartData<"line", (number | Point | null)[], string> | undefined>(undefined);
    const [weightFlightsData, setWeightFlightsData] = useState<ChartData<"line", (number | Point | null)[], string> | undefined>(undefined);
    const [humidityData, setHumidityData] = useState<ChartData<"line", (number | Point | null)[], string> | undefined>(undefined);
    const [wetData, setWetData] = useState<ChartData<"line", (number | Point | null)[], string> | undefined>(undefined);
    const [selectedPeriod, setSelectedPeriod] = useState<ICustomOption | undefined>({ value: "1", desc: "This week" });
    const [selectedUnit, setSelectedUnit] = useState<TimeUnit>("day");

    // refs
    const tempChartRef = useRef<ChartJSOrUndefined<"line", (number | Point | null)[], string>>(null);
    const weightChartRef = useRef<ChartJSOrUndefined<"line", (number | Point | null)[], string>>(null);
    const humidityChartRef = useRef<ChartJSOrUndefined<"line", (number | Point | null)[], string>>(null);
    const wetChartRef = useRef<ChartJSOrUndefined<"line", (number | Point | null)[], string>>(null);

    // snackbar
    const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
    const [messageSnackbar, setMessageSnackbar] = useState<string>("");
    const [responseType, setResponseType] = useState<"success" | "error">("success");

    const periods: ICustomOption[] = [
        { value: "0", desc: t("beehiveDetailsPage.tabs.selectTodayLabel") },
        { value: "1", desc: t("beehiveDetailsPage.tabs.selectThisWeekLabel") },
        { value: "2", desc: t("beehiveDetailsPage.tabs.selectThisMonthLabel") },
        { value: "3", desc: t("beehiveDetailsPage.tabs.selectLast3MonthsLabel") },
        { value: "4", desc: t("beehiveDetailsPage.tabs.selectLast6MonthsLabel") },
        { value: "5", desc: t("beehiveDetailsPage.tabs.selectChooseDatesLabel") }
    ];

    const tempChartOptions: ChartOptions<'line'> = {
        aspectRatio: 2,
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        animation: false,
        plugins: {
            zoom: {
                limits: {
                    x: {
                        min: "original",
                        max: "original"
                    },
                    y: {
                        min: "original",
                        max: "original"
                    },
                    y1: {
                        min: "original",
                        max: "original"
                    },
                },
                pan: {
                    enabled: true,
                    mode: 'xy',
                    modifierKey: "ctrl",
                },
                zoom: {
                    pinch: {
                        enabled: true,
                    },
                    drag: {
                        enabled: true,
                    },
                    wheel: {
                        enabled: true,
                    },
                    mode: 'x',
                }
            },
        },
        scales: {
            x: {
                type: 'time',
                title: {
                    display: true,
                    text: selectedUnit === "hour" ? t("beehiveDetailsPage.tabs.hoursLabel") :
                        selectedUnit === "day" ? t("beehiveDetailsPage.tabs.daysLabel") :
                            t("beehiveDetailsPage.tabs.monthsLabel"),
                    font: { weight: 'bold' }
                },
                time: {
                    unit: selectedUnit,
                    displayFormats: {
                        hour: locale === "en-EN" ? "hh:mm aa" : "HH:mm",
                    },
                    tooltipFormat: locale === "en-EN" ? "PPPP - hh:mm aa" : "PPPP - HH:mm"
                },
                adapters: {
                    date: {
                        locale: locale === "it-IT" ? it : locale === "sl-SI" ? sl : enGB,
                    }
                },
            },
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                title: {
                    display: true,
                    text: t("beehiveDetailsPage.tabs.degreesLabel"),
                    font: { weight: 'bold' }
                },
            },
        }
    };

    const weightChartOptions: ChartOptions<'line'> = {
        ...tempChartOptions,
        scales: {
            ...tempChartOptions.scales,
            y: {
                ...tempChartOptions.scales?.y,
                title: {
                    display: true,
                    text: t("beehiveDetailsPage.tabs.kilogramsLabel"),
                    font: { weight: 'bold' }
                },
            },
            y1: {
                type: 'linear',
                display: true,
                position: 'right',
                title: {
                    display: true,
                    text: t("beehiveDetailsPage.tabs.flightsLabel"),
                    font: { weight: 'bold' }
                },
            },
        }
    };

    const wetChartOptions: ChartOptions<'line'> = {
        ...tempChartOptions,
        scales: {
            ...tempChartOptions.scales,
            y: {
                ...tempChartOptions.scales?.y,
                title: {
                    display: false
                },
                ticks: {
                    callback: (value, _index, _values) => {
                        return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                    }
                }
            }
        }
    };

    const humidityChartOptions: ChartOptions<'line'> = {
        ...tempChartOptions,
        scales: {
            ...tempChartOptions.scales,
            y: {
                ...tempChartOptions.scales?.y,
                title: {
                    display: true,
                    text: t("beehiveDetailsPage.tabs.percentageLabel"),
                    font: { weight: 'bold' }
                },
            },
        }
    };

    const convertUTCToLocalDate = (timestamp: string) => {
        const currentDate = new Date(timestamp)
        const offset = new Date().getTimezoneOffset();
        currentDate.setMinutes(currentDate.getMinutes() - offset);
        return currentDate
    }

    const getCurrentBeehiveStats = async (id: string, from: string, to: string) => {
        try {
            const response = await beehiveStatsService.getAllForCharts(id, from, to);
            let stats: IBeehiveStats[] = [];
            [...response.content.reverse()].forEach((stat, index, array) => {
                const prevTimestamp = new Date(array[index === 0 ? 0 : index - 1].timestamp).getTime();
                const currTimestamp = new Date(stat.timestamp).getTime();
                if (currTimestamp !== prevTimestamp)
                    if (currTimestamp - prevTimestamp > 3600000) {
                        stats.push({ ...stat, temperature: { internal: null, external: null }, weight: null, flyghtsNumber: null })
                        stats.push(stat)
                    } else {
                        stats.push(stat)
                    }
            })
            setCurrentBeehiveStats(stats);
            setTemperatureData({
                labels: stats.map(stat => format(convertUTCToLocalDate(stat.timestamp), "yyyy-MM-dd'T'HH:mm:ss'", { locale: language === "it-IT" ? it : language === "sl-SI" ? sl : enGB })),
                datasets: [
                    {
                        label: t("beehiveDetailsPage.tabs.externalTemperatureLabel") + " (°C)",
                        data: stats.map(stat => stat.temperature.external),
                        backgroundColor: "#008200",
                        borderColor: "#2db415",
                        showLine: true,
                        pointRadius: 0,
                        spanGaps: false,
                    },
                    {
                        label: t("beehiveDetailsPage.tabs.internalTemperatureLabel") + " (°C)",
                        data: stats.map(stat => stat.temperature.internal),
                        backgroundColor: "#ea701a",
                        borderColor: "#f98c19",
                        showLine: true,
                        pointRadius: 0,
                        spanGaps: false
                    }
                ]
            });
            setWeightFlightsData({
                labels: stats.map(stat => format(convertUTCToLocalDate(stat.timestamp), "yyyy-MM-dd'T'HH:mm:ss'", { locale: language === "it-IT" ? it : language === "sl-SI" ? sl : enGB })),
                datasets: [
                    {
                        label: t("beehiveDetailsPage.tabs.weightLabel") + " (kg)",
                        data: stats.map(stat => stat.weight),
                        backgroundColor: "#008200",
                        borderColor: "#2db415",
                        yAxisID: 'y',
                        showLine: true,
                        pointRadius: 0,
                        spanGaps: false,
                    },
                    {
                        label: t("beehiveDetailsPage.tabs.flightsLabel"),
                        data: stats.map(stat => stat.flyghtsNumber),
                        backgroundColor: "#ea701a",
                        borderColor: "#f98c19",
                        showLine: true,
                        pointRadius: 0,
                        yAxisID: 'y1',
                        spanGaps: false,
                    }
                ]
            });
            setHumidityData({
                labels: stats.map(stat => format(convertUTCToLocalDate(stat.timestamp), "yyyy-MM-dd'T'HH:mm:ss'", { locale: language === "it-IT" ? it : language === "sl-SI" ? sl : enGB })),
                datasets: [
                    {
                        label: t("beehiveDetailsPage.tabs.externalHumidityLabel") + " (%)",
                        data: stats.map(stat => stat.humidity.external),
                        backgroundColor: "#008200",
                        borderColor: "#2db415",
                        showLine: true,
                        pointRadius: 0,
                        spanGaps: false
                    },
                    {
                        label: t("beehiveDetailsPage.tabs.internalHumidityLabel") + " (%)",
                        data: stats.map(stat => stat.humidity.internal),
                        backgroundColor: "#ea701a",
                        borderColor: "#f98c19",
                        showLine: true,
                        pointRadius: 0,
                        spanGaps: false
                    }
                ]
            });
            setWetData({
                labels: stats.map(stat => format(convertUTCToLocalDate(stat.timestamp), "yyyy-MM-dd'T'HH:mm:ss'", { locale: language === "it-IT" ? it : language === "sl-SI" ? sl : enGB })),
                datasets: [
                    {
                        label: t("beehiveDetailsPage.tabs.wetLabel"),
                        data: stats.map(stat => stat.wet),
                        backgroundColor: "#008200",
                        borderColor: "#2db415",
                        showLine: true,
                        pointRadius: 0,
                        spanGaps: false,
                    }
                ]
            });
        }
        catch (error) {
            setOpenSnackbar(true);
            setResponseType("error");
            if (error instanceof AxiosError) {
                setMessageSnackbar(error.response?.data.title)
            } else {
                setMessageSnackbar(t("genericError"));
            }
        }
    }

    useEffect(() => {
        setTemperatureData({
            ...temperatureData,
            datasets: temperatureData?.datasets ? temperatureData.datasets.map((ds, i) => {
                return {
                    ...ds,
                    label: i === 0 ? t("beehiveDetailsPage.tabs.externalTemperatureLabel") + " (°C)" : t("beehiveDetailsPage.tabs.internalTemperatureLabel") + " (°C)"
                }
            }) : []
        })
        setWeightFlightsData({
            ...weightFlightsData,
            datasets: weightFlightsData?.datasets ? weightFlightsData.datasets.map((ds, i) => {
                return {
                    ...ds,
                    label: i === 0 ? t("beehiveDetailsPage.tabs.weightLabel") + " (kg)" : t("beehiveDetailsPage.tabs.flightsLabel")
                }
            }) : []
        })
        setHumidityData({
            ...humidityData,
            datasets: humidityData?.datasets ? humidityData.datasets.map((ds, i) => {
                return {
                    ...ds,
                    label: i === 0 ? t("beehiveDetailsPage.tabs.externalHumidityLabel") + " (%)" : t("beehiveDetailsPage.tabs.internalHumidityLabel") + " (%)"
                }
            }) : []
        })
        setWetData({
            ...wetData,
            datasets: wetData?.datasets ? wetData.datasets.map(ds => { return { ...ds, label: t("beehiveDetailsPage.tabs.wetChartTitle") } }) : []
        })
    }, [language])

    useEffect(() => {
        if (locale !== language) {
            setLanguage(locale)
        }
    }, [locale, language])

    useEffect(() => {
        setShowDatePickers(false)
        if (beehiveId && selectedPeriod) {
            switch (selectedPeriod.value.toString()) {
                case "1": {
                    const today = new Date();
                    const startOfLastWeek = format(new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000), "yyyy-MM-dd HH:mm");
                    const endOfLastWeek = format(today, "yyyy-MM-dd HH:mm");
                    getCurrentBeehiveStats(beehiveId, startOfLastWeek, endOfLastWeek);
                    setSelectedUnit("day");
                    break;
                }
                case "2": {
                    const today = new Date();
                    const startOfLastMonth = format(new Date(today.getTime() - 31 * 24 * 60 * 60 * 1000), "yyyy-MM-dd HH:mm");
                    const endOfLastMonth = format(today, "yyyy-MM-dd HH:mm");
                    getCurrentBeehiveStats(beehiveId, startOfLastMonth, endOfLastMonth);
                    setSelectedUnit("day");
                    break;
                }
                case "3": {
                    const today = new Date();
                    const twoMonthsAgo = subMonths(today, 2);
                    const startOfThreeMonthsAgo = format(startOfMonth(twoMonthsAgo), "yyyy-MM-dd HH:mm");
                    const endOfThreeMonths = format(endOfMonth(today), "yyyy-MM-dd HH:mm");
                    getCurrentBeehiveStats(beehiveId, startOfThreeMonthsAgo, endOfThreeMonths);
                    setSelectedUnit("month");
                    break;
                }
                case "4": {
                    const today = new Date();
                    const fiveMonthsAgo = subMonths(today, 5);
                    const startOfFiveMonthsAgo = format(startOfMonth(fiveMonthsAgo), "yyyy-MM-dd HH:mm");
                    const endOfFiveMonths = format(endOfMonth(today), "yyyy-MM-dd HH:mm");
                    getCurrentBeehiveStats(beehiveId, startOfFiveMonthsAgo, endOfFiveMonths);
                    setSelectedUnit("month");
                    break;
                }
                case "5": {
                    setShowDatePickers(true)
                    break;
                }
                default: {
                    const today = new Date();
                    const startOfToday = format(startOfDay(today), "yyyy-MM-dd HH:mm");
                    const endOfToday = format(endOfDay(today), "yyyy-MM-dd HH:mm");
                    getCurrentBeehiveStats(beehiveId, startOfToday, endOfToday);
                    setSelectedUnit("hour");
                    break;
                }
            }
        }
    }, [beehiveId, selectedPeriod])

    useEffect(() => {
        if (beehiveId && startDate && endDate && startDate.getTime() <= endDate.getTime()) {
            const start = format(startDate, "yyyy-MM-dd HH:mm");
            const end = format(endDate, "yyyy-MM-dd HH:mm");
            getCurrentBeehiveStats(beehiveId, start, end);
            const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
            if (diffDays === 0)
                setSelectedUnit("hour");
            if (diffDays > 0 && diffDays <= 14)
                setSelectedUnit("day");
            if (diffDays > 14 && diffDays <= 31)
                setSelectedUnit("week");
            if (diffDays > 31)
                setSelectedUnit("month");
        }
    }, [startDate, endDate])

    const onSelectChange = (e: SelectChangeEvent) => setSelectedPeriod(periods.find(period => period.value === e.target.value));

    const handleTempResetZoom = () => { tempChartRef.current?.resetZoom(); }

    const handleWeightResetZoom = () => { weightChartRef.current?.resetZoom(); }

    const handleHumidityResetZoom = () => { humidityChartRef.current?.resetZoom(); }

    const handleWetResetZoom = () => { wetChartRef.current?.resetZoom(); }

    return <Box sx={{ py: 2 }}>
        <CustomSnackbar
            type={responseType}
            message={messageSnackbar}
            open={openSnackbar}
            onClose={() => setOpenSnackbar(false)}
        />
        <>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                {t("beehiveDetailsPage.tabs.chartsTabTitle")}
            </Typography>
            <Box sx={{ display: "flex" }}>
                <FormControl size="small" sx={{ minWidth: 200, mb: 2 }}>
                    <InputLabel id="period-select-label">
                        {t("beehiveDetailsPage.tabs.selectPeriodPlaceholder")}
                    </InputLabel>
                    <Select
                        id="period-select"
                        value={selectedPeriod?.value.toString() || ""}
                        name="period"
                        onChange={onSelectChange}
                        labelId="period-select-label"
                        label="Select the period"
                    >
                        <MenuItem value="">
                            <em>{t("beehiveDetailsPage.tabs.genericSelectLabel")}</em>
                        </MenuItem>
                        {
                            periods && periods.map(
                                (option) => (
                                    <MenuItem
                                        key={option.value + "-" + option.desc}
                                        value={option.value}
                                    >
                                        {option.desc}
                                    </MenuItem>
                                )
                            )
                        }
                    </Select>
                </FormControl>
                {
                    showDatePickers && <Box sx={{ display: "flex", ml: 2 }}>
                        <DatePicker
                            slotProps={{ textField: { size: 'small' } }}
                            label={t("beehiveDetailsPage.tabs.startDatePicker")}
                            sx={{ mr: 2 }}
                            value={startDate}
                            onChange={(value) => setStartDate(value)}
                        />
                        <DatePicker
                            slotProps={{ textField: { size: 'small' } }}
                            label={t("beehiveDetailsPage.tabs.endDatePicker")}
                            value={endDate}
                            onChange={(value) => setEndDate(value)}
                        />
                    </Box>
                }
            </Box>
            {
                startDate && endDate && startDate.getTime() > endDate.getTime() &&
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Error color="error" />
                    <Typography color="error">
                        {t("beehiveDetailsPage.tabs.datesError")}
                    </Typography>
                </Box>
            }
            {
                loading && !parentDialogOpen ? <CustomLoader /> :
                    selectedPeriod ?
                        selectedPeriod.value !== "5" || (
                            selectedPeriod.value === "5" && startDate && endDate && startDate.getTime() < endDate.getTime()
                        ) ?
                            <>
                                {
                                    currentBeehiveStats.length > 0 && temperatureData && temperatureData.datasets.length > 0 &&
                                    <Paper sx={{ mb: 2, p: 2 }}>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            {t("beehiveDetailsPage.tabs.temperatureChartTitle")}
                                        </Typography>
                                        <Typography variant="body2" sx={{ my: 1 }}>
                                            {t("beehiveDetailsPage.tabs.temperatureChartSubtitle")}
                                        </Typography>
                                        {
                                            temperatureData && <Box sx={{ position: "relative", height: largeMediumScreen ? "50vh" : "35vh", width: "96%" }}>
                                                <Line redraw ref={tempChartRef} data={temperatureData} options={tempChartOptions} />
                                            </Box>
                                        }
                                        <Box sx={{ mt: 1, display: "flex", justifyContent: "center" }}>
                                            <Button variant="outlined" onClick={handleTempResetZoom}>{t("beehiveDetailsPage.tabs.resetZoomButtonLabel")}</Button>
                                        </Box>
                                    </Paper>
                                }
                                {
                                    currentBeehiveStats.length > 0 &&
                                    weightFlightsData && weightFlightsData.datasets.length > 0 &&
                                    <Paper sx={{ my: 2, p: 2 }}>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            {t("beehiveDetailsPage.tabs.weightFlightsChartTitle")}
                                        </Typography>
                                        <Typography variant="body2" sx={{ my: 1 }}>
                                            {t("beehiveDetailsPage.tabs.weightFlightsChartSubtitle")}
                                        </Typography>
                                        {
                                            weightFlightsData && <Box sx={{ position: "relative", height: largeMediumScreen ? "50vh" : "35vh", width: "96%" }}>
                                                <Line ref={weightChartRef} data={weightFlightsData} options={weightChartOptions} />
                                            </Box>
                                        }
                                        <Box sx={{ mt: 1, display: "flex", justifyContent: "center" }}>
                                            <Button variant="outlined" onClick={handleWeightResetZoom}>{t("beehiveDetailsPage.tabs.resetZoomButtonLabel")}</Button>
                                        </Box>
                                    </Paper>
                                }
                                {
                                    currentBeehiveStats.length > 0 &&
                                    beehive && beehive.externalProviderCode === "slovenia" &&
                                    humidityData && humidityData.datasets.length > 0 &&
                                    <Paper sx={{ my: 2, p: 2 }}>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            {t("beehiveDetailsPage.tabs.humidityChartTitle")}
                                        </Typography>
                                        <Typography variant="body2" sx={{ my: 1 }}>
                                            {t("beehiveDetailsPage.tabs.humidityChartSubtitle")}
                                        </Typography>
                                        {
                                            humidityData && <Box sx={{ position: "relative", height: largeMediumScreen ? "50vh" : "35vh", width: "96%" }}>
                                                <Line ref={humidityChartRef} data={humidityData} options={humidityChartOptions} />
                                            </Box>
                                        }
                                        <Box sx={{ mt: 1, display: "flex", justifyContent: "center" }}>
                                            <Button variant="outlined" onClick={handleHumidityResetZoom}>{t("beehiveDetailsPage.tabs.resetZoomButtonLabel")}</Button>
                                        </Box>
                                    </Paper>
                                }
                                {
                                    currentBeehiveStats.length > 0 &&
                                    beehive && beehive.externalProviderCode === "melixa" &&
                                    wetData && wetData.datasets.length > 0 &&
                                    <Paper sx={{ my: 2, p: 2, position: "relative" }} >
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            {t("beehiveDetailsPage.tabs.wetChartTitle")}
                                        </Typography>
                                        <Typography variant="body2" sx={{ my: 1 }}>
                                            {t("beehiveDetailsPage.tabs.wetChartSubtitle")}
                                        </Typography>
                                        {
                                            wetData && <Box sx={{ position: "relative", height: largeMediumScreen ? "50vh" : "35vh", width: "96%" }}>
                                                <Line ref={wetChartRef} data={wetData} options={wetChartOptions} />
                                            </Box>
                                        }
                                        <Box sx={{ mt: 1, display: "flex", justifyContent: "center" }}>
                                            <Button variant="outlined" onClick={handleWetResetZoom}>{t("beehiveDetailsPage.tabs.resetZoomButtonLabel")}</Button>
                                        </Box>
                                    </Paper>
                                }
                                {
                                    currentBeehiveStats.length === 0 && <Typography sx={{ fontWeight: 600, display: 'flex', alignItems: "center" }}>
                                        <Block sx={{ mr: 1 }} />
                                        {t("beehivesPage.noDataLabel")}
                                    </Typography>
                                }
                            </>
                            : <></>
                        : <></>
            }
        </>
    </Box>
}

export default withTranslation()(ChartsTab);